/**
 * h5ads.js — Monetag Rewarded Interstitial service.
 *
 * NOTE: The filename is kept for backwards-compatibility with the ~20
 * components that already import from here. The underlying ad network
 * is Monetag (vignette.min.js, zone 11606704), NOT Google H5.
 *
 * SDK loader lives in /public/index.html. After the loader fetches
 * vignette.min.js, it exposes a global `show_11606704()` that returns
 * a Promise:
 *   • resolves when the user watched the rewarded ad to completion
 *   • rejects when the user closed early, no fill, or SDK missing
 *
 * We fire-and-forget `/api/analytics/ad-event` so the admin dashboard
 * keeps collecting the same completion / fill-rate metrics.
 */

import { api } from "@/lib/api";

const MONETAG_ZONE = "302827";
const PLACEMENT_TIMEOUT_MS = 30_000;   // hard cap so a hung ad can't freeze the UI
export const SPONSOR_SECONDS = 15;
let _inited = false;

// Zone 302827 is a tag.min.js (auto-serve) zone with no show_<zone>() fn, so we
// inject it on the player's click and grant the reward after a fixed break.
function _sponsoredBreak(placement) {
  return new Promise((resolve) => {
    if (!document.querySelector(`script[data-zone="${MONETAG_ZONE}"]`)) {
      const s = document.createElement("script");
      s.src = "https://quge5.com/88/tag.min.js";
      s.async = true;
      s.dataset.zone = MONETAG_ZONE;
      s.setAttribute("data-cfasync", "false");
      document.body.appendChild(s);
    }
    window.__adTagDirty = true;
    _log("AD_LOADED", { placement, mode: "sponsored_break" });
    setTimeout(() => {
      _log("REWARD_EARNED", { placement, mode: "sponsored_break" });
      resolve({ rewarded: true, sponsored: true });
    }, SPONSOR_SECONDS * 1000);
  });
}

/** Log ad lifecycle events to console + backend endpoint for fill-rate analytics. */
function _log(event, payload = {}) {
  // eslint-disable-next-line no-console
  console.info(`[monetag] ${event}`, payload);
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/analytics/ad-event`;
    fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event, ...payload }),
      keepalive: true,
    }).catch(() => {});
  } catch { /* ignore */ }
}

/** Detect whether the Monetag SDK is actually loaded in this browser. */
function _isAvailable() {
  return typeof window !== "undefined" && typeof window[`show_${MONETAG_ZONE}`] === "function";
}

/**
 * Initialize — Monetag auto-loads via the <script> in index.html, so this is
 * really just a one-time "mark init + log" shim. Kept exported so existing
 * callers (`App.js`, `adManager.js`) don't need to change.
 */
export function initH5Ads() {
  if (_inited || typeof window === "undefined") return;
  _inited = true;
  _log("ADS_INIT", { provider: "monetag", zone: MONETAG_ZONE });
}

/**
 * Show a rewarded video ad.
 * @param {Object} opts
 * @param {string} opts.placement — e.g. "continue_on_death", "boss_revive", "daily_bonus".
 * @returns {Promise<{rewarded: boolean, reason?: string}>}
 *   `rewarded: true`  — user watched to completion; caller may call backend to grant.
 *   `rewarded: false` — user dismissed / SDK unavailable / no fill. `reason` explains.
 */
export async function showRewarded(opts = {}) {
  const token = await api
    .post("/ads/sponsor/start", { placement: opts.placement || "continue_on_death" })
    .then((r) => r?.ad_token || null)
    .catch(() => null);
  const res = await _showInner(opts);
  return { ...res, token };
}

function _showInner({ placement = "continue_on_death" } = {}) {
  initH5Ads();

  return new Promise((resolve) => {
    if (!_isAvailable()) {
      _sponsoredBreak(placement).then(resolve);
      return;
    }

    let settled = false;
    const settle = (result) => {
      if (settled) return;
      settled = true;
      resolve(result);
    };

    // Hard timeout — if the SDK promise never settles we won't hang the UI.
    const timer = setTimeout(() => {
      _log("AD_TIMEOUT", { placement });
      settle({ rewarded: false, reason: "timeout" });
    }, PLACEMENT_TIMEOUT_MS);

    _log("AD_LOADED", { placement });

    try {
      // Monetag SDK pattern: window.show_<zone>() → Promise.
      // Resolves on reward earned, rejects on dismissal / no fill / network error.
      window[`show_${MONETAG_ZONE}`]()
        .then(() => {
          clearTimeout(timer);
          _log("REWARD_EARNED", { placement });
          settle({ rewarded: true });
        })
        .catch((err) => {
          clearTimeout(timer);
          const reason = String(err || "no_ad").slice(0, 60);
          _log("AD_FAILED", { placement, reason });
          settle({ rewarded: false, reason });
        });
    } catch (e) {
      clearTimeout(timer);
      _log("AD_ERROR", { placement, message: String(e) });
      settle({ rewarded: false, reason: "exception" });
    }
  });
}

/**
 * Best-effort readiness check. Returns true if the Monetag SDK global is present.
 */
export function isRewardedReady() {
  return typeof window !== "undefined";
}

/** Exposed for the diagnostic page. */
export const AD_PROVIDER = "monetag";
export const AD_ZONE = MONETAG_ZONE;
export const AD_SDK_URL = "https://quge5.com/88/tag.min.js";
